import { Check, ChevronRight, CornerUpRight, Loader2, MessageSquare, X } from 'lucide-react'

import { useSupportChatSessions } from '@/lib/api/queries'
import type { ChatSessionSummary } from '@/lib/api/support-chat-types'

interface SessionHistoryListProps {
  eventId: number
  /** Called when the operator clicks a session card. The drawer is
   *  responsible for fetching the full session (with messages) and
   *  swapping its state. */
  onSelect: (sessionId: number) => void
  /** Called when the operator clicks "New chat" — drawer should reset
   *  state and switch back to the live view. */
  onStartNew: () => void
}

/**
 * History panel for the support chat drawer. Lists the current user's
 * past sessions for the active event, newest first. Unended sessions are
 * resumable (input re-enabled); ended sessions open read-only with a
 * banner. The backend hides 0-message placeholder sessions so this list
 * stays clean.
 */
export function SessionHistoryList({ eventId, onSelect, onStartNew }: SessionHistoryListProps) {
  const { data: sessions, isLoading, error } = useSupportChatSessions(eventId)

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
        <p className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          Recent chats
        </p>
        <button
          type="button"
          onClick={onStartNew}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/30"
        >
          + New chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading chats…
          </div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            Couldn&apos;t load past chats: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : !sessions || sessions.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center text-center">
            <MessageSquare className="mb-2 h-6 w-6 text-neutral-400 dark:text-neutral-600" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              No past chats for this event yet.
            </p>
            <button
              type="button"
              onClick={onStartNew}
              className="mt-3 rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-blue-500"
            >
              Start a new chat
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <SessionCard key={s.id} session={s} onSelect={() => onSelect(s.id)} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function SessionCard({ session, onSelect }: { session: ChatSessionSummary; onSelect: () => void }) {
  const ended = session.ended_at !== null
  const label = ended ? 'Open' : 'Resume'

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="group flex w-full items-start gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/40 dark:hover:border-neutral-700 dark:hover:bg-neutral-900"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-neutral-900 dark:text-neutral-100">
              {formatWhen(session.started_at)}
            </span>
            <StatusBadge session={session} />
          </div>
          {(session.summary || session.classification) && (
            <p className="mt-1 line-clamp-2 text-[11px] text-neutral-600 dark:text-neutral-400">
              {session.summary || formatClassification(session.classification)}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-neutral-500 dark:text-neutral-500">
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {session.message_count} {session.message_count === 1 ? 'message' : 'messages'}
            </span>
            {session.classification && session.summary && (
              <>
                <span>·</span>
                <span>{formatClassification(session.classification)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-blue-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-blue-400">
          {label}
          <ChevronRight className="h-3.5 w-3.5" />
        </div>
      </button>
    </li>
  )
}

function StatusBadge({ session }: { session: ChatSessionSummary }) {
  if (!session.ended_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Open
      </span>
    )
  }
  const status = session.resolution_status
  if (status === 'resolved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        <Check className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" />
        Resolved
      </span>
    )
  }
  if (status === 'unresolved') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        <X className="h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
        Unresolved
      </span>
    )
  }
  if (status === 'escalated') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
        <CornerUpRight className="h-2.5 w-2.5 text-blue-600 dark:text-blue-400" />
        Escalated
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
      Ended
    </span>
  )
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const diff = Date.now() - d.getTime()
  const sec = Math.round(diff / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.round(hr / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const CLASSIFICATION_LABELS: Record<string, string> = {
  login_issue: 'Login issue',
  missing_tokens: 'Missing tokens',
  payment_decline: 'Payment decline',
  refund_request: 'Refund request',
  wallet_qr_broken: 'Wallet / QR',
  vendor_dispute: 'Vendor dispute',
  other: 'Other',
}

function formatClassification(c: string | null): string {
  if (!c) return ''
  return CLASSIFICATION_LABELS[c] ?? c
}
